import { SystemStatusLevel } from '@choco-admin/utils';
import { Tooltip } from '@heroui/react';
import React from 'react';

import { useSystemStatus } from '../../hooks/useSystemStatus';

interface StatusConfig {
  dotClass: string;
  label: string;
  pulse: boolean;
}

function getStatusConfig(
  level: SystemStatusLevel,
  ongoingCount: number,
  maintenanceCount: number
): StatusConfig {
  switch (level) {
    case 'operational':
      return {
        dotClass: 'bg-success',
        label: 'All systems operational',
        pulse: true,
      };
    case 'degraded':
      return {
        dotClass: 'bg-warning',
        label: `Degraded — ${ongoingCount} ongoing`,
        pulse: true,
      };
    case 'outage':
      return {
        dotClass: 'bg-danger',
        label: `Outage — ${ongoingCount} ongoing`,
        pulse: true,
      };
    case 'maintenance':
      return {
        dotClass: 'bg-primary',
        label: `Maintenance — ${maintenanceCount} in progress`,
        pulse: false,
      };
    case 'unknown':
    default:
      // Unconfigured or unreachable — never pretend systems are healthy.
      return {
        dotClass: 'bg-default-400',
        label: 'No connection to incident.io',
        pulse: false,
      };
  }
}

interface SystemStatusIndicatorProps {
  collapsed: boolean;
}

/**
 * Sidebar footer widget that shows live system status from incident.io.
 *
 * Reads VITE_STATUS_PAGE_URL (the published status page base URL, e.g.
 * https://statuspage.incident.io/bechoco). When unset or unreachable, shows
 * "No connection to incident.io" with a dimmed dot and a tooltip explaining
 * how to connect it — never a fake healthy status.
 *
 * Clicking the indicator opens the status page in a new tab (only when
 * VITE_STATUS_PAGE_URL is configured).
 */
export const SystemStatusIndicator: React.FC<SystemStatusIndicatorProps> = ({
  collapsed,
}) => {
  const {
    level,
    ongoingCount,
    maintenanceCount,
    isStaticFallback,
    statusPageUrl,
  } = useSystemStatus();

  const { dotClass, label, pulse } = getStatusConfig(
    level,
    ongoingCount,
    maintenanceCount
  );

  const handleClick = () => {
    if (statusPageUrl) {
      window.open(statusPageUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const dot = (
    <div
      className={`w-2 h-2 rounded-full flex-shrink-0 ${dotClass} ${
        pulse ? 'animate-pulse' : ''
      }`}
    />
  );

  // In collapsed mode: show just the dot, wrapped in a tooltip.
  if (collapsed) {
    return (
      <div className="flex justify-center">
        <Tooltip
          content={
            isStaticFallback
              ? `${label} (status monitoring not configured)`
              : label
          }
          placement="right"
          delay={300}
          closeDelay={100}
          className="bg-content1 text-content1-foreground border border-divider"
        >
          <button
            type="button"
            onClick={handleClick}
            aria-label={`System status: ${label}`}
            className={`p-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
              statusPageUrl ? 'cursor-pointer' : 'cursor-default'
            }`}
          >
            {dot}
          </button>
        </Tooltip>
      </div>
    );
  }

  // Expanded mode: show the full pill with label.
  const content = (
    <button
      type="button"
      onClick={handleClick}
      aria-label={`System status: ${label}`}
      className={`w-full bg-gradient-to-r from-primary-50 to-secondary-50 border border-primary-200 rounded-lg p-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
        statusPageUrl
          ? 'cursor-pointer hover:from-primary-100 hover:to-secondary-100 transition-colors duration-150'
          : 'cursor-default'
      }`}
    >
      <div className="flex items-center gap-2">
        {dot}
        <span
          className={`text-xs font-medium ${
            isStaticFallback ? 'text-default-400' : 'text-default-700'
          }`}
        >
          {label}
        </span>
      </div>
    </button>
  );

  if (isStaticFallback) {
    return (
      <Tooltip
        content="Status monitoring not configured — add VITE_STATUS_PAGE_URL to connect incident.io"
        placement="top"
        delay={500}
        closeDelay={100}
        className="bg-content1 text-content1-foreground border border-divider max-w-xs"
      >
        {content}
      </Tooltip>
    );
  }

  return content;
};
